import { Component, input, output, computed, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { StepperHeaderComponent, type StepperStep } from '../../../molecules/stepper/stepper-header/stepper-header.component';
import { StepperActionBarComponent } from '../../../molecules/stepper/stepper-action-bar/stepper-action-bar.component';
import { StepHeaderComponent } from '../../../molecules/stepper/step-header/step-header.component';

/**
 * Organism component that combines StepperHeader with progress visualization
 * and StepperActionBar for navigation controls.
 */
@Component({
  selector: 'htx-stepper',
  standalone: true,
  imports: [CommonModule, StepperHeaderComponent, StepperActionBarComponent, StepHeaderComponent],
  templateUrl: './stepper.component.html',
  styleUrl: './stepper.component.scss'
})
export class StepperComponent {
  /**
   * Array of stepper steps with labels, completion status, and optional icons
   */
  steps = input.required<StepperStep[]>();

  /**
   * Initial active step index (0-based). If not provided, internal state is used.
   */
  initialStep = input<number>(0);

  /**
   * Internal step tracking signal for default implementation
   */
  private internalStep = signal<number>(0);

  /**
   * Get the current step (from internal state, initially set by initialStep input)
   */
  currentStep = computed(() => {
    return this.internalStep();
  });

  constructor() {
    // Sync initialStep input to internalStep signal
    effect(() => {
      this.internalStep.set(this.initialStep());
    });
  }

  /**
   * Layout mode for the action bar: 'start', 'between', or 'last-step'
   * Automatically calculated based on currentStep if not provided
   */
  actionBarLayout = input<'start' | 'between' | 'last-step'>('start');

  /**
   * Computed layout based on current step position
   */
  computedActionBarLayout = computed(() => {
    const current = this.currentStep();
    const totalSteps = this.steps().length;

    // Step 0: 'start' layout
    if (current === 0) {
      return 'start';
    }
    // Last step: 'last-step' layout
    if (current === totalSteps - 1) {
      return 'last-step';
    }
    // Middle steps: 'between' layout
    return 'between';
  });

  /**
   * Gap between buttons in the action bar (in rem)
   */
  actionBarGap = input<number>(1);

  /**
   * Whether to show divider in the action bar
   */
  showDivider = input<boolean>(false);

  /**
   * Label for the previous button
   */
  previousLabel = input<string>('Back');

  /**
   * Label for the next button
   */
  nextLabel = input<string>('Next');

  /**
   * Label for the last step button (when layout is 'last-step')
   */
  lastStepLabel = input<string>('Done');

  /**
   * Whether the previous button is disabled
   */
  previousDisabled = input<boolean>(false);

  /**
   * Whether the next button is disabled
   */
  nextDisabled = input<boolean>(false);

  /**
   * Event emitted when the previous button is clicked
   */
  previous = output<void>();

  /**
   * Event emitted when the next button is clicked
   */
  next = output<void>();

  /**
   * Event emitted when the last step button is clicked
   */
  lastStep = output<void>();

  onPrevious(): void {
    const current = this.currentStep();
    if (current > 0) {
      this.internalStep.set(current - 1);
    }
    this.previous.emit();
  }

  onNext(): void {
    const current = this.currentStep();
    const totalSteps = this.steps().length;
    if (current < totalSteps - 1) {
      this.internalStep.set(current + 1);
    }
    this.next.emit();
  }

  onLastStep(): void {
    this.internalStep.set(this.steps().length - 1);
    this.lastStep.emit();
  }
}
